// apps/admin-ui/src/config/tableConfigs.ts
// Table configurations for DataTablePage
// ALL RIGHTS RESERVED
//
// UPDATED 2026-02-01: Fixed endpoints to use /api/v1/admin/* paths
// Added withdrawals table config
// UPDATED 2026-02-01: Enabled withdrawal approve/reject actions (Phase 2)

import type { Permission } from '../utils/rbac';

// =============================================================================
// TYPES
// =============================================================================

export interface ColumnDef {
  key: string;
  label: string;
  sortable?: boolean;
  width?: string;
  align?: 'left' | 'center' | 'right';
  render?: (value: any, row: any) => React.ReactNode;
}

export interface FilterDef {
  key: string;
  label: string;
  type: 'select' | 'text' | 'date' | 'daterange';
  options?: { value: string; label: string }[];
}

export interface ActionDef {
  id: string;
  label: string;
  icon?: string;
  permission: Permission;
  enabled: boolean;  // true = active, false = stub (coming soon)
  variant?: 'primary' | 'secondary' | 'danger';
  handler?: (row: any) => void;
}

export interface TableConfig {
  title: string;
  endpoint: string;
  columns: ColumnDef[];
  filters?: FilterDef[];
  actions?: ActionDef[];
  defaultSort?: { key: string; direction: 'asc' | 'desc' };
  pageSize?: number;
}

// =============================================================================
// TABLE CONFIGURATIONS
// =============================================================================

export const TABLE_CONFIGS: Record<string, TableConfig> = {
  // KYC Queue
  '/admin/kyc': {
    title: 'KYC Queue',
    endpoint: '/api/admin/kyc/queue',
    columns: [
      { key: 'id', label: 'ID', width: '100px' },
      { key: 'email', label: 'User', sortable: true },
      { key: 'type', label: 'Type', width: '100px' },
      { key: 'status', label: 'Status', width: '120px' },
      { key: 'submittedAt', label: 'Submitted', sortable: true, width: '160px' },
      { key: 'assignedTo', label: 'Assigned To', width: '140px' },
    ],
    filters: [
      {
        key: 'status',
        label: 'Status',
        type: 'select',
        options: [
          { value: '', label: 'All' },
          { value: 'pending', label: 'Pending' },
          { value: 'in_review', label: 'In Review' },
          { value: 'approved', label: 'Approved' },
          { value: 'rejected', label: 'Rejected' },
        ],
      },
      {
        key: 'type',
        label: 'Type',
        type: 'select',
        options: [
          { value: '', label: 'All' },
          { value: 'individual', label: 'Individual' },
          { value: 'business', label: 'Business' },
        ],
      },
    ],
    actions: [
      { id: 'approve', label: 'Approve', permission: 'csr:approve', enabled: false, variant: 'primary' },
      { id: 'reject', label: 'Reject', permission: 'csr:reject', enabled: false, variant: 'danger' },
    ],
    defaultSort: { key: 'submittedAt', direction: 'asc' },
    pageSize: 20,
  },

  // AML Alerts
  '/admin/aml': {
    title: 'AML Alerts',
    endpoint: '/api/admin/aml/alerts',
    columns: [
      { key: 'id', label: 'Alert ID', width: '100px' },
      { key: 'userId', label: 'User ID', width: '120px' },
      { key: 'type', label: 'Alert Type', width: '140px' },
      { key: 'severity', label: 'Severity', width: '100px' },
      { key: 'status', label: 'Status', width: '100px' },
      { key: 'createdAt', label: 'Created', sortable: true, width: '160px' },
      { key: 'description', label: 'Description' },
    ],
    filters: [
      {
        key: 'severity',
        label: 'Severity',
        type: 'select',
        options: [
          { value: '', label: 'All' },
          { value: 'critical', label: 'Critical' },
          { value: 'high', label: 'High' },
          { value: 'medium', label: 'Medium' },
          { value: 'low', label: 'Low' },
        ],
      },
      {
        key: 'status',
        label: 'Status',
        type: 'select',
        options: [
          { value: '', label: 'All' },
          { value: 'open', label: 'Open' },
          { value: 'investigating', label: 'Investigating' },
          { value: 'resolved', label: 'Resolved' },
          { value: 'dismissed', label: 'Dismissed' },
        ],
      },
    ],
    actions: [
      { id: 'investigate', label: 'Investigate', permission: 'compliance:kyc_aml_review', enabled: false, variant: 'primary' },
      { id: 'escalate', label: 'Escalate', permission: 'compliance:regulatory_reporting', enabled: false, variant: 'secondary' },
    ],
    defaultSort: { key: 'createdAt', direction: 'desc' },
    pageSize: 20,
  },

  // Mint Requests (Deposits)
  // FIXED: endpoint now points to /api/v1/admin/mint/pending
  '/admin/mints': {
    title: 'Mint Requests',
    endpoint: '/api/v1/admin/mint/pending',
    columns: [
      { key: 'id', label: 'Request ID', width: '120px' },
      { key: 'userId', label: 'User ID', width: '120px' },
      { key: 'userEmail', label: 'User Email', width: '180px' },
      { key: 'amount', label: 'Amount', align: 'right', width: '140px' },
      { key: 'status', label: 'Status', width: '100px' },
      { key: 'createdAt', label: 'Requested', sortable: true, width: '160px' },
      { key: 'source', label: 'Source', width: '120px' },
    ],
    filters: [
      {
        key: 'status',
        label: 'Status',
        type: 'select',
        options: [
          { value: '', label: 'Pending Only' },
          { value: 'all', label: 'All' },
          { value: 'pending', label: 'Pending' },
          { value: 'completed', label: 'Completed' },
          { value: 'failed', label: 'Failed' },
        ],
      },
    ],
    actions: [
      // Deposits auto-complete - no approval needed
      // Keeping empty array for now; could add "view details" action later
    ],
    defaultSort: { key: 'createdAt', direction: 'asc' },
    pageSize: 20,
  },

  // Withdrawal Requests (Burns/Redeems)
  // This is where Alice's $25 withdrawal shows up!
  // UPDATED 2026-02-01: Enabled approve/reject actions
  '/admin/withdrawals': {
    title: 'Withdrawal Requests',
    endpoint: '/api/v1/admin/withdrawals/pending',
    columns: [
      { key: 'id', label: 'Request ID', width: '120px' },
      { key: 'userId', label: 'User ID', width: '120px' },
      { key: 'userEmail', label: 'User Email', width: '180px' },
      { key: 'amount', label: 'Amount', align: 'right', width: '140px' },
      { key: 'fee', label: 'Fee', align: 'right', width: '100px' },
      { key: 'status', label: 'Status', width: '100px' },
      { key: 'createdAt', label: 'Requested', sortable: true, width: '160px' },
      { key: 'destination', label: 'Destination', width: '140px' },
    ],
    filters: [
      {
        key: 'status',
        label: 'Status',
        type: 'select',
        options: [
          { value: '', label: 'Pending Only' },
          { value: 'all', label: 'All' },
          { value: 'pending', label: 'Pending' },
          { value: 'completed', label: 'Completed' },
          { value: 'failed', label: 'Failed' },
        ],
      },
    ],
    actions: [
      // ENABLED in Phase 2 - treasury can approve/reject withdrawals (redeems)
      { id: 'approve', label: 'Approve', permission: 'reserve:authorize_redeem', enabled: true, variant: 'primary' },
      { id: 'reject', label: 'Reject', permission: 'reserve:authorize_redeem', enabled: true, variant: 'danger' },
    ],
    defaultSort: { key: 'createdAt', direction: 'desc' },
    pageSize: 20,
  },

  // Transactions (All transfers)
  // FIXED: endpoint now points to /api/v1/admin/transfers
  '/admin/transactions': {
    title: 'Transactions',
    endpoint: '/api/v1/admin/transfers',
    columns: [
      { key: 'id', label: 'TX ID', width: '140px' },
      { key: 'type', label: 'Type', width: '100px' },
      { key: 'fromUserId', label: 'From', width: '120px' },
      { key: 'toUserId', label: 'To', width: '120px' },
      { key: 'amount', label: 'Amount', align: 'right', width: '140px' },
      { key: 'fee', label: 'Fee', align: 'right', width: '100px' },
      { key: 'status', label: 'Status', width: '100px' },
      { key: 'createdAt', label: 'Date', sortable: true, width: '160px' },
    ],
    filters: [
      {
        key: 'type',
        label: 'Type',
        type: 'select',
        options: [
          { value: '', label: 'All' },
          { value: 'mint', label: 'Mint (Deposit)' },
          { value: 'burn', label: 'Burn (Withdrawal)' },
          { value: 'p2p', label: 'P2P Transfer' },
        ],
      },
      {
        key: 'status',
        label: 'Status',
        type: 'select',
        options: [
          { value: '', label: 'All' },
          { value: 'completed', label: 'Completed' },
          { value: 'pending', label: 'Pending' },
          { value: 'failed', label: 'Failed' },
        ],
      },
    ],
    actions: [],  // Read-only
    defaultSort: { key: 'createdAt', direction: 'desc' },
    pageSize: 50,
  },

  // Vulnerabilities
  '/admin/security/vulns': {
    title: 'Vulnerabilities',
    endpoint: '/api/admin/security/vulnerabilities',
    columns: [
      { key: 'id', label: 'ID', width: '100px' },
      { key: 'title', label: 'Title' },
      { key: 'severity', label: 'Severity', width: '100px' },
      { key: 'status', label: 'Status', width: '120px' },
      { key: 'assignedTo', label: 'Assigned', width: '120px' },
      { key: 'reportedAt', label: 'Reported', sortable: true, width: '140px' },
    ],
    filters: [
      {
        key: 'severity',
        label: 'Severity',
        type: 'select',
        options: [
          { value: '', label: 'All' },
          { value: 'critical', label: 'Critical' },
          { value: 'high', label: 'High' },
          { value: 'medium', label: 'Medium' },
          { value: 'low', label: 'Low' },
        ],
      },
      {
        key: 'status',
        label: 'Status',
        type: 'select',
        options: [
          { value: '', label: 'All' },
          { value: 'open', label: 'Open' },
          { value: 'in_progress', label: 'In Progress' },
          { value: 'resolved', label: 'Resolved' },
        ],
      },
    ],
    actions: [
      { id: 'assign', label: 'Assign', permission: 'system:manage_users', enabled: false, variant: 'primary' },
      { id: 'close', label: 'Close', permission: 'system:manage_users', enabled: false, variant: 'secondary' },
    ],
    defaultSort: { key: 'reportedAt', direction: 'desc' },
    pageSize: 20,
  },

  // Security Alerts
  '/admin/security/alerts': {
    title: 'Security Alerts',
    endpoint: '/api/admin/security/alerts',
    columns: [
      { key: 'id', label: 'ID', width: '100px' },
      { key: 'type', label: 'Type', width: '140px' },
      { key: 'severity', label: 'Severity', width: '100px' },
      { key: 'status', label: 'Status', width: '100px' },
      { key: 'createdAt', label: 'Created', sortable: true, width: '160px' },
      { key: 'description', label: 'Description' },
    ],
    filters: [],
    actions: [],
    defaultSort: { key: 'createdAt', direction: 'desc' },
    pageSize: 20,
  },

  // Contract Deployments
  '/admin/contracts': {
    title: 'Contract Deployments',
    endpoint: '/api/admin/contracts/deployments',
    columns: [
      { key: 'id', label: 'ID', width: '100px' },
      { key: 'contractName', label: 'Contract', width: '160px' },
      { key: 'version', label: 'Version', width: '100px' },
      { key: 'network', label: 'Network', width: '120px' },
      { key: 'address', label: 'Address', width: '160px' },
      { key: 'deployedAt', label: 'Deployed', sortable: true, width: '160px' },
      { key: 'deployedBy', label: 'By', width: '100px' },
    ],
    filters: [
      {
        key: 'network',
        label: 'Network',
        type: 'select',
        options: [
          { value: '', label: 'All' },
          { value: 'ethereum', label: 'Ethereum' },
          { value: 'polygon', label: 'Polygon' },
          { value: 'arbitrum', label: 'Arbitrum' },
        ],
      },
    ],
    actions: [],
    defaultSort: { key: 'deployedAt', direction: 'desc' },
    pageSize: 20,
  },

  // Contract Reviews (LIVE - connected to audit.contract_reviews)
  '/admin/contracts/reviews': {
    title: 'Smart Contract Version Reviews',
    endpoint: '/api/v1/admin/contracts/reviews',
    columns: [
      { key: 'id', label: 'ID', width: '60px' },
      { key: 'versionTag', label: 'Version', width: '90px' },
      { key: 'gitCommitHash', label: 'Commit', width: '80px' },
      { key: 'contractsChanged', label: 'Contracts Changed', width: '280px' },
      { key: 'status', label: 'Status', width: '100px' },
      { key: 'reviewerName', label: 'Reviewer', width: '120px' },
      { key: 'createdAt', label: 'Submitted', sortable: true, width: '140px' },
    ],
    filters: [
      {
        key: 'status',
        label: 'Status',
        type: 'select',
        options: [
          { value: '', label: 'All' },
          { value: 'pending', label: 'Pending' },
          { value: 'in_review', label: 'In Review' },
          { value: 'approved', label: 'Approved' },
          { value: 'rejected', label: 'Rejected' },
        ],
      },
    ],
    actions: [
      { id: 'claim', label: 'Claim', permission: 'contracts:review', enabled: true, variant: 'primary' },
      { id: 'approve', label: 'Approve', permission: 'contracts:review', enabled: true, variant: 'primary' },
      { id: 'reject', label: 'Reject', permission: 'contracts:review', enabled: true, variant: 'danger' },
    ],
    defaultSort: { key: 'createdAt', direction: 'desc' },
    pageSize: 20,
  },

  // Governance Proposals
  '/admin/governance': {
    title: 'Governance Proposals',
    endpoint: '/api/admin/governance/proposals',
    columns: [
      { key: 'id', label: 'ID', width: '100px' },
      { key: 'title', label: 'Title' },
      { key: 'type', label: 'Type', width: '140px' },
      { key: 'status', label: 'Status', width: '100px' },
      { key: 'votesFor', label: 'For', align: 'center', width: '60px' },
      { key: 'votesAgainst', label: 'Against', align: 'center', width: '80px' },
      { key: 'deadline', label: 'Deadline', sortable: true, width: '160px' },
    ],
    filters: [
      {
        key: 'status',
        label: 'Status',
        type: 'select',
        options: [
          { value: '', label: 'All' },
          { value: 'active', label: 'Active' },
          { value: 'passed', label: 'Passed' },
          { value: 'rejected', label: 'Rejected' },
          { value: 'vetoed', label: 'Vetoed' },
        ],
      },
      {
        key: 'type',
        label: 'Type',
        type: 'select',
        options: [
          { value: '', label: 'All' },
          { value: 'parameter', label: 'Parameter Change' },
          { value: 'upgrade', label: 'Contract Upgrade' },
          { value: 'treasury', label: 'Treasury Action' },
        ],
      },
    ],
    actions: [
      { id: 'vote', label: 'Vote', permission: 'governance:vote', enabled: false, variant: 'primary' },
      { id: 'veto', label: 'Veto', permission: 'governance:veto', enabled: false, variant: 'danger' },
    ],
    defaultSort: { key: 'deadline', direction: 'asc' },
    pageSize: 20,
  },

  // Audit Reports (placeholder)
  '/admin/audit/reports': {
    title: 'Audit Reports',
    endpoint: '/api/admin/audit/reports',
    columns: [
      { key: 'id', label: 'ID', width: '100px' },
      { key: 'title', label: 'Title' },
      { key: 'type', label: 'Type', width: '120px' },
      { key: 'status', label: 'Status', width: '120px' },
      { key: 'auditor', label: 'Auditor', width: '140px' },
      { key: 'completedAt', label: 'Completed', sortable: true, width: '160px' },
    ],
    filters: [],
    actions: [],
    defaultSort: { key: 'completedAt', direction: 'desc' },
    pageSize: 20,
  },

  // Attestation Status (placeholder)
  '/admin/audit/attestation': {
    title: 'Monthly Attestations',
    endpoint: '/api/admin/audit/attestation',
    columns: [
      { key: 'id', label: 'ID', width: '100px' },
      { key: 'month', label: 'Month', width: '140px' },
      { key: 'status', label: 'Status', width: '120px' },
      { key: 'attestedBy', label: 'Attested By', width: '140px' },
      { key: 'attestedAt', label: 'Attested', sortable: true, width: '160px' },
    ],
    filters: [],
    actions: [],
    defaultSort: { key: 'attestedAt', direction: 'desc' },
    pageSize: 20,
  },

  // Custody Holdings (placeholder)
  '/admin/custody/holdings': {
    title: 'Segregated Asset Holdings',
    endpoint: '/api/admin/custody/holdings',
    columns: [
      { key: 'id', label: 'ID', width: '100px' },
      { key: 'assetType', label: 'Asset Type', width: '140px' },
      { key: 'amount', label: 'Amount', align: 'right', width: '160px' },
      { key: 'custodian', label: 'Custodian', width: '140px' },
      { key: 'verifiedAt', label: 'Verified', sortable: true, width: '160px' },
    ],
    filters: [],
    actions: [],
    defaultSort: { key: 'verifiedAt', direction: 'desc' },
    pageSize: 20,
  },
};

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Get table configuration by route path
 */
export function getTableConfig(path: string): TableConfig | null {
  return TABLE_CONFIGS[path] || null;
}

/**
 * Get all table routes
 */
export function getTableRoutes(): string[] {
  return Object.keys(TABLE_CONFIGS);
}
